import { parseISO } from 'date-fns';

import { parseDate } from 'lib/date';
import Summary from 'models/aiArtifacts/Summary';
import AIJob, { AIJobOptions } from 'models/AIJob';
import Attr, { AttrOptions } from 'models/Attr';
import Group, { GroupOptions } from 'models/Group';

import AIArtifact, { AIArtifactOptions } from './AIArtifact';
import Attachment, { AttachmentOptions } from './Attachment';

export enum ReviewType {
  ADMISSION = 'admission',
  ALTCS_APPLICATION = 'altcs_application',
  AUTHORIZATION = 'authorization',
  COORDINATE_REQUEST = 'coordinate_request',
  PROJECTED_DISCHARGE = 'projected_discharge',
  REFERRAL = 'referral',
  SAFE_RETURN = 'safe_return',
  SERVICE_REFUSAL = 'service_refusal',
}

export enum ReviewStatus {
  PENDING = 'pending',
  ACCEPTED = 'accepted',
  REJECTED = 'rejected',
  CANCELED = 'canceled',
}

export type UserInfo = {
  id: string;
  fullName: string;
};

export type GroupInfo = {
  id: string;
  name: string;
};

export type BaseReviewDataOptions = {
  statusUpdatedAt: string | null;
  createdByGroup: GroupInfo | null;
  reviewedByGroup: GroupInfo | null;
};

export type BaseReviewData = {
  statusUpdatedAt: Date | null;
};

export interface ReviewOptions {
  id: string;
  locationEpisodeId: string;
  groupId: string | null;
  group: Partial<GroupOptions> | null;
  status: ReviewStatus;
  type: ReviewType;
  createdById: string;
  reviewedById: string | null;
  createdBy: UserInfo;
  reviewedBy: UserInfo | null;
  // Models inheriting from the `Review` class should define the shape of `data`
  data: Partial<BaseReviewDataOptions>;
  createdAt: string | null;
  updatedAt: string | null;
  attrs: Partial<AttrOptions>[];
  attachments: Partial<AttachmentOptions>[];
  attachmentCount?: number;
  totalAttachmentCount?: number;
  aiArtifacts: Partial<AIArtifactOptions>[];
  summaryJobForCurrentAttachments?: AIJobOptions | null;
}

export function getDefaults(): ReviewOptions {
  return {
    id: '',
    locationEpisodeId: '',
    groupId: null,
    group: null,
    status: ReviewStatus.PENDING,
    type: ReviewType.ALTCS_APPLICATION,
    createdById: '',
    createdBy: {
      id: '',
      fullName: '',
    },
    reviewedById: null,
    reviewedBy: null,
    data: {
      statusUpdatedAt: null,
    },
    createdAt: null,
    updatedAt: null,
    attrs: [],
    attachments: [],
    aiArtifacts: [],
    summaryJobForCurrentAttachments: null,
  };
}
/**
 * @class Review
 * @classdesc Represents a review in the system
 * @property {string} id - The review's id
 * @property {string | Date} createdAt - The review's creation date
 * @property {string} createdBy - The review's creator
 * @property {Record<string, any>} data - The review's data
 * @property {Partial<GroupOptions>} group - The review's group
 * @property {string} locationEpisodeId - The review's location episode id
 * @property {string} reviewedBy - The review's reviewer
 * @property {string} status - The review's status
 * @property {string} type - The review's type
 * @param {Partial<ReviewOptions>} [options={}]
 * @example const review = new Review({ id: '123' });
 */
export default class Review {
  id: string;
  locationEpisodeId: string;
  groupId: string | null;
  group: Group | null;
  status: ReviewStatus;
  type: ReviewType;
  createdById: string;
  createdBy: UserInfo;
  reviewedById: string | null;
  reviewedBy: UserInfo | null;
  // Models inheriting from this class should define the shape of `data`
  data: Record<string, any>;
  createdAt: Date | null;
  updatedAt: Date | null;
  attrs: Attr[];
  attachments: Attachment[];
  attachmentCount?: number;
  totalAttachmentCount?: number;
  aiArtifacts: AIArtifact[];
  summaryJobForCurrentAttachments: AIJob | null;

  constructor(options: Partial<ReviewOptions> = {}) {
    const opts = { ...getDefaults(), ...options };

    this.id = opts.id;
    this.locationEpisodeId = opts.locationEpisodeId;
    this.groupId = opts.groupId;
    this.group = opts.group ? new Group(opts.group) : null;
    this.status = opts.status;
    this.type = opts.type;
    this.createdById = opts.createdById;
    this.createdBy = opts.createdBy;
    this.reviewedById = opts.reviewedById;
    this.reviewedBy = opts.reviewedBy;
    this.data = {
      ...opts.data,
      statusUpdatedAt: parseDate(opts.data.statusUpdatedAt) ?? null,
    };
    this.createdAt = parseDate(opts.createdAt) ?? null;
    this.updatedAt = parseDate(opts.updatedAt) ?? null;
    this.attrs = opts.attrs?.map((attr) => new Attr(attr)) ?? [];
    this.attachments = opts.attachments?.map((attachment) => new Attachment(attachment)) ?? [];
    this.attachmentCount = opts.attachmentCount;
    this.totalAttachmentCount = opts.totalAttachmentCount;
    this.aiArtifacts = opts.aiArtifacts?.map((aiArtifact) => new AIArtifact(aiArtifact)) ?? [];
    this.summaryJobForCurrentAttachments = opts.summaryJobForCurrentAttachments
      ? new AIJob(opts.summaryJobForCurrentAttachments)
      : null;
  }

  get pending() {
    return this.status === ReviewStatus.PENDING;
  }

  get active() {
    return this.pending;
  }

  get completed() {
    return !this.active;
  }

  get accepted() {
    return this.status === ReviewStatus.ACCEPTED;
  }

  get rejected() {
    return this.status === ReviewStatus.REJECTED;
  }

  get canceled() {
    return this.status === ReviewStatus.CANCELED;
  }

  get summaryJob(): AIJob | null {
    return this.summaryJobForCurrentAttachments;
  }

  get summary(): Summary | undefined {
    return this.summaryJob?.aiArtifact as Summary | undefined;
  }

  get attrValues() {
    return this.attrs.flatMap((attr) => attr?.attrValues || []) ?? [];
  }
}

export const parseProjectedDischarge = (projectedDischarge?: string | Date) => {
  if (!projectedDischarge) return null;

  const date =
    projectedDischarge instanceof Date ? new Date(projectedDischarge.getTime()) : parseISO(projectedDischarge);

  date.setHours(23, 59, 59, 999);
  return date;
};
